import React, { Component, createElement } from 'react';
import classNames from 'classnames';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import isFunction from 'lodash/isFunction';
import Icon from '../icon';
import Tooltip from '../tooltip';
import Progress from '../progress';
import Animate from '../animate';
import PopConfirm from '../popconfirm';
import { ProgressType } from '../progress/enum';
import { getFileSizeStr, getFileType, isImageUrl, previewImage } from './utils';
import CompressedfileIcon from './icon-svg/compressedfileIcon';
import DocIcon from './icon-svg/docIcon';
import FileuploadIcon from './icon-svg/fileuploadIcon';
import ImageIcon from './icon-svg/imageIcon';
import PdfIcon from './icon-svg/pdfIcon';
import XlsIcon from './icon-svg/xlsIcon';
import { Size } from '../_util/enum';
import ConfigContext from '../config-provider/ConfigContext';
// a little function to help us with reordering the result
const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
};
function defaultRenderIcon(file, listType, prefixCls) {
    if (listType === 'picture' || listType === 'picture-card') {
        return React.createElement(Icon, { type: "insert_drive_file", className: `${prefixCls}-list-item-icon` });
    }
    if (file.status === 'uploading') {
        return React.createElement(Progress, { key: 'loading', type: ProgressType.loading, size: Size.small });
    }
    const filetype = getFileType(file.name);
    switch (filetype) {
        case 'compressedfile':
            return React.createElement(CompressedfileIcon, { className: `${prefixCls}-icon-file` });
        case 'doc':
            return React.createElement(DocIcon, { className: `${prefixCls}-icon-file` });
        case 'image':
            return React.createElement(ImageIcon, { className: `${prefixCls}-icon-file` });
        case 'pdf':
            return React.createElement(PdfIcon, { className: `${prefixCls}-icon-file` });
        case 'xls':
            return React.createElement(XlsIcon, { className: `${prefixCls}-icon-file` });
        default:
            return React.createElement(FileuploadIcon, { className: `${prefixCls}-icon-file` });
    }
}
export default class UploadList extends Component {
    constructor() {
        super(...arguments);
        this.handleClose = (file) => {
            const { onRemove } = this.props;
            if (onRemove) {
                onRemove(file);
            }
        };
        this.handlePreview = (file, e) => {
            const { onPreview } = this.props;
            if (!onPreview) {
                return;
            }
            e.preventDefault();
            return onPreview(file);
        };
        /**
         * @param {UploadFile} file
         * @param {React.SyntheticEvent<HTMLElement>} e
         */
        this.handleReUpload = (file, e) => {
            const { onReUpload } = this.props;
            if (!onReUpload) {
                return;
            }
            e.preventDefault();
            onReUpload(file);
        };
        this.handleReUploadConfirm = (fileTarget, e) => {
            if (fileTarget.status === 'error') {
                this.handleReUpload(fileTarget, e);
            }
            else if (fileTarget.status && ['success', 'done', 'removed'].includes(fileTarget.status)) {
                const { getUploadRef, setReplaceReuploadItem } = this.props;
                const uploadRef = getUploadRef();
                if (uploadRef) {
                    const { fileInput } = uploadRef.uploader;
                    setReplaceReuploadItem(fileTarget);
                    fileInput.click();
                }
            }
        };
        /**
         * 拖拽事件
         * @param result
         */
        this.onDragEnd = (result) => {
            const { items, onDragEnd } = this.props;
            // dropped outside the list
            if (!result.destination) {
                return;
            }
            const dragItems = reorder(items, result.source.index, result.destination.index);
            onDragEnd(dragItems);
        };
    }
    static get contextType() {
        return ConfigContext;
    }
    componentDidUpdate() {
        const { listType } = this.props;
        if (listType !== 'picture' && listType !== 'picture-card') {
            return;
        }
        const { items, previewFile } = this.props;
        (items || []).forEach(file => {
            if (typeof document === 'undefined' ||
                typeof window === 'undefined' ||
                !window.FileReader ||
                !window.File ||
                !(file.originFileObj instanceof File || file.originFileObj instanceof Blob) ||
                file.thumbUrl !== undefined) {
                return;
            }
            file.thumbUrl = '';
            if (previewFile) {
                previewFile(file.originFileObj).then((previewDataUrl) => {
                    // Need append '' to avoid dead loop
                    file.thumbUrl = previewDataUrl || '';
                    this.forceUpdate();
                });
            }
        });
    }
    render() {
        const { prefixCls: customizePrefixCls, items = [], listType, showPreviewIcon, showRemoveIcon, showDownloadIcon, showReUploadIcon, removePopConfirmTitle, reUploadText, reUploadPopConfirmTitle, locale, dragUploadList, showFileSize, getCustomFilenameTitle, downloadPropsIntercept, tooltipPrefixCls, popconfirmProps, renderIcon = defaultRenderIcon, } = this.props;
        const { getPrefixCls } = this.context;
        const prefixCls = getPrefixCls('upload', customizePrefixCls);
        const list = items.map((file, index) => {
            let progress;
            let icon = renderIcon(file, listType, prefixCls);
            const stat = {
                isImg: isImageUrl(file),
                isUploading: file.status === 'uploading',
                isError: file.status === 'error',
                isPictureCard: listType === 'picture-card',
            };
            if (listType === 'picture' || stat.isPictureCard) {
                if (stat.isPictureCard && stat.isUploading) {
                    icon = React.createElement("div", { className: `${prefixCls}-list-item-uploading-text` }, locale.uploading);
                }
                else if (listType === 'picture' && stat.isUploading) {
                    icon = React.createElement(Progress, { key: 'loading', type: ProgressType.loading, size: Size.small });
                }
                else if (!file.thumbUrl && !file.url) {
                    icon = React.createElement(Icon, { className: `${prefixCls}-list-item-thumbnail`, type: "picture" });
                }
                else {
                    const thumbnail = stat.isImg ? (React.createElement("img", { src: file.thumbUrl || file.url, alt: file.name })) : icon;
                    icon = (React.createElement("a", { className: `${prefixCls}-list-item-thumbnail`, onClick: e => this.handlePreview(file, e), href: file.url || file.thumbUrl, target: "_blank", rel: "noopener noreferrer" }, thumbnail));
                }
            }
            if (stat.isUploading) {
                const { progressAttr } = this.props;
                // show loading icon if upload progress listener is disabled
                const loadingProgress = 'percent' in file ? (React.createElement(Progress, Object.assign({ type: ProgressType.line }, progressAttr, { percent: file.percent }))) : null;
                progress = (React.createElement("div", { className: `${prefixCls}-list-item-progress`, key: "progress" }, loadingProgress));
            }
            const message = file.response && typeof file.response === 'string' ? file.response : ((file.error && file.error.statusText) || locale.uploadError);
            const preview = file.url ? (React.createElement("a", Object.assign({ href: file.url }, file.linkProps, { target: "_blank", rel: "noopener noreferrer", className: `${prefixCls}-list-item-name`, onClick: e => this.handlePreview(file, e), title: file.name }), file.name)) : (React.createElement("span", { className: `${prefixCls}-list-item-name`, onClick: e => this.handlePreview(file, e), title: file.name }, file.name));
            const style = file.url || file.thumbUrl
                ? undefined
                : {
                    pointerEvents: 'none',
                    opacity: 0.5,
                };
            const previewIcon = (isFunction(showPreviewIcon)
                ? showPreviewIcon(file)
                : stat.isImg && showPreviewIcon) ? (React.createElement("a", { href: file.url || file.thumbUrl, target: "_blank", rel: "noopener noreferrer", style: style, onClick: e => this.handlePreview(file, e), title: locale.previewFile },
                React.createElement(Icon, { type: "visibility" }))) : null;
            let showReUploadIconType;
            if (isFunction(showReUploadIcon)) {
                const customReUploadIcon = showReUploadIcon(file, listType);
                showReUploadIconType = customReUploadIcon ? customReUploadIcon === 'text' ? 'text' : 'icon' : undefined;
            }
            else if (showReUploadIcon) {
                showReUploadIconType = showReUploadIcon === 'text' ? 'text' : 'icon';
            }
            const pictureCardReUploadIconOrText = showReUploadIconType && !stat.isUploading ? (React.createElement(PopConfirm, Object.assign({}, popconfirmProps, { title: reUploadPopConfirmTitle || locale.confirmReUpload, onConfirm: (e) => {
                    this.handleReUploadConfirm(file, e);
                } }), showReUploadIconType === 'icon' ? (React.createElement(Icon, { type: "file_upload", title: reUploadText })) : (stat.isError ?
                React.createElement(Tooltip, { prefixCls: tooltipPrefixCls, title: message },
                    React.createElement("span", { className: `${prefixCls}-list-item-reupload-${listType}`, title: reUploadText }, locale.reUpload)) :
                React.createElement("span", { className: `${prefixCls}-list-item-reupload-${listType}`, title: reUploadText }, locale.reUpload)))) : null;
            const removeIcon = showRemoveIcon ? (React.createElement(PopConfirm, Object.assign({}, popconfirmProps, { title: removePopConfirmTitle || locale.confirmRemove, onConfirm: () => {
                    this.handleClose(file);
                } }),
                React.createElement(Icon, { type: stat.isPictureCard ? 'delete' : 'close', className: stat.isPictureCard ? `${prefixCls}-list-item-action-remove` : undefined, title: locale.removeFile }))) : null;
            const downloadLinkProps = {
                ...file.linkProps,
                rel: 'noopener noreferrer',
            };
            if (stat.isError) {
                downloadLinkProps.style = { color: '#f5222d' };
            }
            if (!(stat.isError || stat.isUploading)) {
                downloadLinkProps.href = file.url || file.thumbUrl;
                downloadLinkProps.target = '_blank';
                if (downloadLinkProps.href) {
                    downloadLinkProps.filename = file.name;
                }
            }
            const downloadIcon = !stat.isError && !stat.isUploading && (isFunction(showDownloadIcon) ? showDownloadIcon(file) : showDownloadIcon)
                && React.createElement("a", Object.assign({}, downloadPropsIntercept(downloadLinkProps), { style: style }),
                    React.createElement(Icon, { type: "get_app" }));
            const actionsClass = classNames(`${prefixCls}-list-item-actions`, {
                [`${prefixCls}-list-item-actions-reupload-text`]: showReUploadIconType === 'text' && stat.isError,
            });
            const fileName = (stat.isPictureCard && !stat.isUploading) ? (React.createElement(Tooltip, { prefixCls: tooltipPrefixCls, title: getCustomFilenameTitle ? getCustomFilenameTitle(file) : file.name, placement: "bottom" }, createElement(downloadLinkProps.href ? 'a' : 'span', {
                className: `${prefixCls}-list-item-file-name`,
                ...downloadPropsIntercept(downloadLinkProps),
            }, file.name))) : null;
            const filesizeStr = getFileSizeStr(file.size);
            const fileSize = (showFileSize && filesizeStr !== '' && listType === 'text') ? (React.createElement("span", { className: `${prefixCls}-list-item-info-filesize` }, filesizeStr)) : null;
            const iconAndPreview = stat.isPictureCard ? (React.createElement("span", { className: `${prefixCls}-list-item-picture-card` }, icon)) : React.createElement("span", { className: `${prefixCls}-list-item-text` },
                icon,
                preview);
            const iconAndPreviewTooltip = stat.isError ? (React.createElement(Tooltip, { prefixCls: tooltipPrefixCls, title: message }, iconAndPreview)) : (iconAndPreview);
            const reUpload = showReUploadIconType && (listType === 'text' || listType === 'picture') && !stat.isUploading ? (React.createElement(PopConfirm, Object.assign({}, popconfirmProps, { title: reUploadPopConfirmTitle || locale.confirmReUpload, onConfirm: (e) => {
                    this.handleReUploadConfirm(file, e);
                } }),
                React.createElement("span", { className: `${prefixCls}-list-item-reupload-${listType}`, title: reUploadText }, locale.reUpload))) : null;
            const getActions = () => {
                if (!stat.isUploading) {
                    if (stat.isPictureCard) {
                        return (React.createElement("div", { className: actionsClass },
                            React.createElement("span", { className: `${prefixCls}-reupload-action` }, pictureCardReUploadIconOrText),
                            React.createElement("span", { className: `${prefixCls}-other-actions` },
                                stat.isError || previewIcon,
                                downloadIcon,
                                removeIcon)));
                    }
                    return (React.createElement("div", { className: `${prefixCls}-actions` },
                        reUpload,
                        stat.isError || previewIcon,
                        downloadIcon,
                        removeIcon));
                }
                return React.createElement("div", { className: `${prefixCls}-actions` }, removeIcon);
            };
            const listItemInfo = (React.createElement("div", { className: `${prefixCls}-list-item-info` },
                React.createElement("span", { className: `${prefixCls}-list-item-span` },
                    iconAndPreviewTooltip,
                    fileName,
                    fileSize,
                    getActions()),
                React.createElement(Animate, { transitionName: "fade", component: "" }, progress)));
            const infoUploadingClass = classNames({
                [`${prefixCls}-list-item`]: true,
                [`${prefixCls}-list-item-${file.status}`]: true,
                [`${prefixCls}-list-item-error-reupload`]: file.status === 'error' && showReUploadIconType,
                [`${prefixCls}-list-item-done-reupload`]: file.status === 'done' && showReUploadIconType,
            });
            if (dragUploadList) {
                return (React.createElement(Draggable, { key: file.uid, draggableId: String(file.uid), index: index }, provided => (React.createElement("div", Object.assign({ className: infoUploadingClass, key: file.uid, ref: provided.innerRef }, provided.draggableProps, provided.dragHandleProps), listItemInfo))));
            }
            return (React.createElement("div", { className: infoUploadingClass, key: file.uid }, listItemInfo));
        });
        const listClassNames = classNames({
            [`${prefixCls}-list`]: true,
            [`${prefixCls}-list-${listType}`]: true,
            [`${prefixCls}-list-drag`]: dragUploadList,
        });
        const animationDirection = listType === 'picture-card' ? 'animate-inline' : 'animate';
        if (dragUploadList) {
            return (React.createElement(DragDropContext, { onDragEnd: this.onDragEnd },
                React.createElement(Droppable, { droppableId: "droppable", direction: "horizontal" }, (provided, snapshot) => (React.createElement("div", Object.assign({ ref: provided.innerRef, style: {
                        background: snapshot.isDraggingOver ? '#f2f9f4' : '',
                        border: snapshot.isDraggingOver ? '2px dashed #1ab16f' : '',
                        display: 'inline-flex',
                        maxWidth: '100%',
                        flexWrap: 'wrap',
                        overflow: 'auto',
                    } }, provided.droppableProps, { className: listClassNames }),
                    list,
                    provided.placeholder)))));
        }
        return (React.createElement(Animate, { transitionName: `${prefixCls}-${animationDirection}`, component: "div", className: listClassNames }, list));
    }
}
UploadList.displayName = 'UploadList';
UploadList.defaultProps = {
    listType: 'text',
    progressAttr: {
        strokeWidth: 2,
        showInfo: false,
    },
    previewFile: previewImage,
    showRemoveIcon: true,
    showPreviewIcon: true,
    dragUploadList: false,
    showFileSize: false,
    showDownloadIcon: true,
    showReUploadIcon: false,
    downloadPropsIntercept: o => o,
};
